import React, { useCallback, useEffect, useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useStatus, useData, isLoading, combineStatuses, useClearFetch } from 'resift';
import _get from 'lodash/get';
import showSnackbar from 'helpers/showSnackbar';
import * as Types from 'pages/Pages/typings';
import createEntityType from 'fetches/createEntityType';
import makeEntityTypesFetch from 'store/configuration/actions/entityTypesFetch';
import useEntityTypes from 'helpers/useEntityTypes';

import SlideDialog from 'molecules/SlideDialog';
import ChoosePageTypeSlide from './ChoosePageTypeSlide';
import AttributeSlide from './AttributeSlide';
import IndependentSlide from './IndependentSlide';
import ExistingAttributeSlide from './ExistingAttributeSlide';
import NewAttributeSlide from './NewAttributeSlide';

interface Props {
  newAttributeType?: 'page' | 'pageCollection';
  open: boolean;
  onSetOpen: (status: boolean) => void;
}

function PageTypesDialog({ newAttributeType, open, onSetOpen }: Props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const clearFetch = useClearFetch();
  const entityTypesFetch = makeEntityTypesFetch();
  const entityTypesFetchStatus = useStatus(entityTypesFetch);
  const [defaultSlideIndex, setDefaultSlideIndex] = useState(newAttributeType ? 4 : 0);
  const createEntityTypeStatus = useStatus(createEntityType);
  const newEntityTypeData = useData(createEntityType);
  const combinedStatus = combineStatuses(createEntityTypeStatus, entityTypesFetchStatus);
  const loading = isLoading(combinedStatus);
  const entityTypes = useEntityTypes();
  const entityId = _get(newEntityTypeData, 'id');
  const entityIdInEntityTypes = _get(entityTypes, entityId);

  const onSave = useCallback(
    async (data: Types.NewEntityType) => {
      !newAttributeType && setDefaultSlideIndex(0);
      await dispatch(createEntityType(data));
      await dispatch(entityTypesFetch());
    },
    [newAttributeType],
  );

  useEffect(() => {
    if (entityIdInEntityTypes) {
      onSetOpen(false);
      history.push(`/admin/pages/${entityId}`);
    }
  }, [entityId, entityIdInEntityTypes]);

  useEffect(() => {
    return () => {
      clearFetch(createEntityType);
    };
  }, []);

  showSnackbar({
    status: createEntityTypeStatus,
    variant: 'success',
    message: 'Your page type has been created.',
    errorMessage: 'There was an error creating your page type. Please try again.',
  });

  return (
    <SlideDialog
      defaultSlideIndex={defaultSlideIndex}
      dialogProps={{ 'data-testid': 'new page type dialog' }}
      dialogClassName={classes.dialogRoot}
      slideContainerClassName={classes.slideContainer}
      open={open}
      setDialogOpen={onSetOpen}
    >
      {(slideIndex, setSlideIndex) =>
        open
          ? [
              <ChoosePageTypeSlide
                classes={classes}
                onSetOpen={onSetOpen}
                setSlideIndex={setSlideIndex}
              />,
              <AttributeSlide
                classes={classes}
                onSetOpen={onSetOpen}
                setSlideIndex={setSlideIndex}
              />,
              <IndependentSlide
                loading={loading}
                onSetOpen={onSetOpen}
                onSave={onSave}
                setSlideIndex={setSlideIndex}
              />,
              <ExistingAttributeSlide
                classes={classes}
                loading={loading}
                onSave={onSave}
                onSetOpen={onSetOpen}
                setSlideIndex={setSlideIndex}
              />,
              <NewAttributeSlide
                classes={classes}
                loading={loading}
                selectedAttribute={newAttributeType}
                onSave={onSave}
                onSetOpen={onSetOpen}
                setSlideIndex={setSlideIndex}
              />,
            ]
          : []
      }
    </SlideDialog>
  );
}

const useStyles = makeStyles((theme: any) => ({
  dialogRoot: {
    minWidth: 600,
    minHeight: 500,
    overflowX: 'hidden',
  },
  slideContainer: {
    minHeight: 550,
  },
  paper: {
    boxShadow: theme.shadows[2],
  },
  slide: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    width: '50%',
    cursor: 'pointer',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: 0,
  },
  marginRight3: {
    marginRight: theme.spacing(3),
  },
  marginRight: {
    marginRight: theme.spacing(),
  },
  marginBottom: {
    marginBottom: theme.spacing(),
  },
  marginBottom2: {
    marginBottom: theme.spacing(2),
  },
  cardImage: {
    height: 126,
    width: 264,
    marginBottom: theme.spacing(2),
  },
  textAlignCenter: {
    textAlign: 'center',
  },
  cardDiv: {
    display: 'flex',
    cursor: 'pointer',
  },
  activeCard: {
    border: `2px solid ${theme.palette.secondary.main}`,
    borderRadius: theme.spacing(0.5),
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
  },
}));

export default PageTypesDialog;
